// src/pages/Admin/Vote/VoteResultsModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getVoteResults } from "../../../services/voteServices";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from "recharts";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import { FaTimes, FaChartPie, FaChartBar } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";

const PASTEL = [
  "#8ab4f8", // bleu léger
  "#f6c26b", // ambre doux
  "#81c995", // vert menthe
  "#f28b82", // corail doux
  "#c58af9", // violet pastel
  "#80d8d1", // turquoise clair
  "#ffb3c1", // rose pastel
  "#aed581", // vert clair
  "#ffd8a8", // pêche
  "#b39ddb", // mauve
  "#90caf9",
  "#ffe082",
];

// utilitaire : tronquer un libellé long
const ellipsize = (s = "", n = 28) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

// calcule le total des voix (ou sélections pour multiple)
const sumCounts = (choices = []) =>
  choices.reduce((acc, c) => acc + (Number(c?.count) || 0), 0);

// Tooltip custom pour chart
const TooltipContent = ({ active, payload, label, total }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const val = Number(p.value) || 0;
  const pct = total > 0 ? Math.round((val * 100) / total) : 0;
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 shadow text-sm">
      <div className="font-semibold">{p?.name || label}</div>
      <div className="text-slate-600 dark:text-slate-300">
        {val} ({pct}%)
      </div>
    </div>
  );
};

export default function VoteResultsModal({ isOpen, onClose, voteId, voteTitle }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // pour développer/replier les réponses libres par question
  const [expandedOpen, setExpandedOpen] = useState({}); // { [questionId]: bool }

  useEffect(() => {
    if (!isOpen || !voteId) return;
    setExpandedOpen({});
    (async () => {
      setLoading(true);
      try {
        const data = await getVoteResults(voteId);
        setResults(Array.isArray(data?.results) ? data.results : []);
      } catch (e) {
        toast.error("Erreur lors du chargement des résultats");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, voteId]);

  const hasData = useMemo(() => results && results.length > 0, [results]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay cliquable */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div className="relative w-full max-w-5xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70 max-h-[90vh] flex flex-col">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h2 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Résultats : <span className="italic font-light">{voteTitle}</span>
          </h2>
        </div>

        {/* Contenu */}
        <div className="p-5 sm:p-6 space-y-5 overflow-y-auto">
          {loading && (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40 animate-pulse"
                />
              ))}
            </div>
          )}

          {!loading && !hasData && (
            <div className="text-center py-12">
              <div className="mx-auto h-14 w-14 rounded-full bg-slate-200 dark:bg-slate-800" />
              <h4 className="mt-4 text-slate-800 dark:text-slate-200 font-semibold">
                Aucun résultat disponible
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Les questions et les réponses s’afficheront ici dès qu’il y en aura.
              </p>
            </div>
          )}

          {!loading &&
            hasData &&
            results.map((q, index) => {
              const total = sumCounts(q?.choices || []);
              const choicesSorted = (q?.choices || [])
                .map((c) => ({ ...c, count: Number(c?.count) || 0 }))
                .sort((a, b) => b.count - a.count);

              const isChoiceType = q?.type === "single" || q?.type === "multiple";
              const isOpenType = q?.type === "open";

              const openResponses = Array.isArray(q?.responses)
                ? q.responses
                : [];
              // autoriser string ou {response_text}
              const openTexts = openResponses.map((r) =>
                typeof r === "string" ? r : r?.response_text || ""
              );
              const maxCollapsed = 6;
              const expanded = !!expandedOpen[q.question_id];
              const shownOpen = expanded ? openTexts : openTexts.slice(0, maxCollapsed);

              return (
                <div
                  key={q.question_id}
                  className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/50 overflow-hidden"
                >
                  {/* Bandeau question */}
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold">
                        {index + 1}
                      </span>
                      <div className="text-sm text-slate-700 dark:text-slate-200">
                        <ReactQuill
                          value={q?.question_text || ""}
                          readOnly
                          theme="bubble"
                          className="prose prose-sm max-w-none dark:prose-invert"
                        />
                      </div>
                    </div>
                    <span className="text-xs rounded-full px-2.5 py-1 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 bg-white/70 dark:bg-slate-900/40">
                      {q?.type === "single"
                        ? "Réponse unique"
                        : q?.type === "multiple"
                        ? "Réponses multiples"
                        : "Réponse libre"}
                    </span>
                  </div>

                  {/* Contenu question */}
                  <div className="p-4 sm:p-5">
                    {isChoiceType && choicesSorted.length > 0 && (
                      <>
                        {/* Résumé total */}
                        <div className="mb-3 text-xs text-slate-600 dark:text-slate-400">
                          Total réponses comptées :{" "}
                          <span className="font-semibold text-slate-800 dark:text-slate-200">
                            {total}
                          </span>
                        </div>

                        {/* CHARTS */}
                        {q.type === "single" ? (
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Donut */}
                            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <FaChartPie className="text-slate-500" />
                                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                  Répartition (donut)
                                </h4>
                              </div>
                              <div style={{ width: "100%", height: 280 }}>
                                <ResponsiveContainer>
                                  <PieChart>
                                    <Pie
                                      data={choicesSorted}
                                      dataKey="count"
                                      nameKey="choice_text"
                                      innerRadius="55%"
                                      outerRadius="85%"
                                      paddingAngle={2}
                                    >
                                      {choicesSorted.map((_, i) => (
                                        <Cell
                                          key={i}
                                          fill={PASTEL[i % PASTEL.length]}
                                        />
                                      ))}
                                      <LabelList
                                        position="outside"
                                        formatter={(val, entry) => {
                                          const pct =
                                            total > 0
                                              ? Math.round(
                                                  (Number(val) * 100) / total
                                                )
                                              : 0;
                                          return `${pct}%`;
                                        }}
                                      />
                                    </Pie>
                                    <Tooltip
                                      content={({ active, payload }) =>
                                        TooltipContent({
                                          active,
                                          payload,
                                          total,
                                        })
                                      }
                                    />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            </div>

                            {/* Légende lue / lisible */}
                            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-3">
                              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                                Détails par choix
                              </h4>
                              <ul className="space-y-2">
                                {choicesSorted.map((c, i) => {
                                  const pct =
                                    total > 0
                                      ? Math.round((c.count * 100) / total)
                                      : 0;
                                  return (
                                    <li
                                      key={c.id || i}
                                      className="flex items-center justify-between gap-3"
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span
                                          className="h-3 w-3 rounded"
                                          style={{
                                            background: PASTEL[i % PASTEL.length],
                                          }}
                                        />
                                        <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                                          {c.choice_text}
                                        </span>
                                      </div>
                                      <span className="text-sm font-medium text-slate-800 dark:text-slate-100 shrink-0">
                                        {c.count} ({pct}%)
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          </div>
                        ) : (
                          // multiple → barres horizontales
                          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <FaChartBar className="text-slate-500" />
                              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                Sélections (barres horizontales)
                              </h4>
                            </div>
                            <div style={{ width: "100%", height: Math.max(220, choicesSorted.length * 36) }}>
                              <ResponsiveContainer>
                                <BarChart
                                  layout="vertical"
                                  data={choicesSorted.map((c) => ({
                                    ...c,
                                    choice_text: ellipsize(c.choice_text, 40),
                                  }))}
                                  margin={{ left: 20, right: 20 }}
                                >
                                  <XAxis type="number" />
                                  <YAxis
                                    dataKey="choice_text"
                                    type="category"
                                    width={140}
                                  />
                                  <Tooltip
                                    content={({ active, payload, label }) =>
                                      active && payload?.length ? (
                                        <TooltipContent
                                          active={active}
                                          payload={[
                                            {
                                              ...payload[0],
                                              name: label,
                                              value: payload[0].value,
                                            },
                                          ]}
                                          label={label}
                                          total={total}
                                        />
                                      ) : null
                                    }
                                  />
                                  <Bar dataKey="count" radius={[6, 6, 6, 6]}>
                                    {choicesSorted.map((_, i) => (
                                      <Cell
                                        key={i}
                                        fill={PASTEL[i % PASTEL.length]}
                                      />
                                    ))}
                                    <LabelList
                                      dataKey="count"
                                      position="right"
                                      className="text-sm"
                                    />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                              Total sélections :{" "}
                              <span className="font-semibold text-slate-800 dark:text-slate-200">
                                {total}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {isOpenType && (
                      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-3">
                        <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                          Réponses libres ({openTexts.length})
                        </h4>
                        {openTexts.length === 0 ? (
                          <p className="text-sm italic text-slate-500 dark:text-slate-400">
                            Aucune réponse.
                          </p>
                        ) : (
                          <>
                            <ul className="space-y-2">
                              {shownOpen.map((txt, i) => (
                                <li
                                  key={`${q.question_id}-${i}`}
                                  className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/40 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
                                >
                                  {txt}
                                </li>
                              ))}
                            </ul>
                            {openTexts.length > maxCollapsed && (
                              <div className="mt-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedOpen((prev) => ({
                                      ...prev,
                                      [q.question_id]: !prev[q.question_id],
                                    }))
                                  }
                                  className="text-xs font-medium underline text-slate-700 dark:text-slate-300"
                                >
                                  {expanded ? "Voir moins" : "Voir tout"}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          {/* Pied de modale */}
          <div className="flex justify-center">
            <Button text="Fermer" icon={FaTimes} onClick={onClose} className="gap-2 !rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}
